import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  Platform,
} from 'react-native';
import { launchImageLibrary, launchCamera, ImagePickerResponse, MediaType } from 'react-native-image-picker';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PostItemMediaItem } from '@/types/residents/marketplaceListing';
import { SVG_COMPONENTS } from '@/assets/admin';
import { CarouselDots } from '../CarouselDots';
import { showAlert, showConfirmWithButtons } from '@/utils/common/alert';
import { StepHeader, postItemStyles } from './shared';

const UPLOAD_AREA_HEIGHT = 281;
const MAX_PHOTOS = 10;
const MAX_FILE_SIZE_MB = 2;

const TrashIcon = SVG_COMPONENTS.TRASH;
const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;
const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

interface StepMediaProps {
  mediaItems: PostItemMediaItem[];
  onMediaItemsChange: (items: PostItemMediaItem[]) => void;
  title?: string;
}

export const StepMedia: React.FC<StepMediaProps> = ({
  mediaItems,
  onMediaItemsChange,
  title = 'Add photo or video',
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const hasImages = mediaItems.length > 0;

  const handleImageResponse = useCallback((response: ImagePickerResponse) => {
    if (response.didCancel) return;
    if (response.errorCode || response.errorMessage) {
      showAlert('Image Error', response.errorMessage || 'Could not access the camera or photo library.');
      return;
    }

    if (response.assets) {
      const baseTimestamp = Date.now();
      const availableSlots = Math.max(MAX_PHOTOS - mediaItems.length, 0);
      
      const validAssets = response.assets.filter(asset => {
        if (asset.fileSize && asset.fileSize > MAX_FILE_SIZE_MB * 1024 * 1024) {
          showAlert('File Too Large', `File exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
          return false;
        }
        return true;
      });
      
      if (validAssets.length === 0) return;
      
      const newPhotos: PostItemMediaItem[] = validAssets.slice(0, availableSlots).map((asset, index) => ({
        id: baseTimestamp + index + Math.random() * 1000,
        uri: asset.uri || '',
        type: asset.type || 'image/jpeg',
        name: asset.fileName || `image_${baseTimestamp}_${index}.jpg`,
        size: asset.fileSize,
      }));

      if (newPhotos.length < validAssets.length) {
        showAlert('Photo Limit Reached', `Only ${newPhotos.length} photo(s) added. Maximum is ${MAX_PHOTOS}.`);
      }

      onMediaItemsChange([...mediaItems, ...newPhotos]);
    }
  }, [mediaItems, onMediaItemsChange]);

  const openCamera = useCallback(async () => {
    if (Platform.OS === 'android') {
      const { PermissionsAndroid } = require('react-native');
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
      );
      if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
        showAlert('Camera Permission Required', 'Please grant camera access in your device settings to take photos.');
        return;
      }
    }

    const options = {
      mediaType: 'photo' as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
    };

    launchCamera(options, handleImageResponse);
  }, [handleImageResponse]);

  const openImageLibrary = useCallback(() => {
    const availableSlots = Math.max(MAX_PHOTOS - mediaItems.length, 0);
    
    if (Platform.OS === 'web') {
      const input = (globalThis as any).document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/gif,image/webp';
      input.multiple = availableSlots > 1;
      input.onchange = () => {
        const files = Array.from(input.files || []);
        
        const validFiles = files.filter((file: any) => {
          if (!file.type.startsWith('image/')) return false;
          if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            showAlert('File Too Large', `${file.name} exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
            return false;
          }
          return true;
        });
        
        if (validFiles.length === 0) return;
        
        const trimmed = validFiles.slice(0, availableSlots);
        if (trimmed.length < validFiles.length) {
          showAlert('Too Many Photos', `You can only upload up to ${MAX_PHOTOS} photos.`);
        }
        
        const baseTimestamp = Date.now();
        const newPhotos: PostItemMediaItem[] = trimmed.map((file, index) => ({
          id: baseTimestamp + index + Math.random() * 1000,
          uri: URL.createObjectURL(file as any),
          type: (file as any).type || 'image/jpeg',
          name: (file as any).name || `image_${baseTimestamp}_${index}.jpg`,
          size: (file as any).size,
          file: file as any,
        }));
        
        onMediaItemsChange([...mediaItems, ...newPhotos]);
      };
      input.click();
      return;
    }

    const options = {
      mediaType: 'photo' as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
      selectionLimit: availableSlots > 1 ? 0 : 1,
    };
    launchImageLibrary(options, handleImageResponse);
  }, [mediaItems, onMediaItemsChange, handleImageResponse]);

  const handleImagePick = useCallback(() => {
    if (Platform.OS === 'web') {
      openImageLibrary();
      return;
    }
    showConfirmWithButtons(
      'Select Image',
      'Choose how you want to select an image',
      'Photo Library',
      'Camera',
      () => openImageLibrary(),
      () => openCamera()
    );
  }, [openImageLibrary, openCamera]);

  const handleDeleteImage = useCallback(() => {
    if (mediaItems.length === 0) return;
    
    const updatedImages = mediaItems.filter((_, index) => index !== currentImageIndex);
    onMediaItemsChange(updatedImages);
    
    if (currentImageIndex >= updatedImages.length && updatedImages.length > 0) {
      setCurrentImageIndex(updatedImages.length - 1);
    } else if (updatedImages.length === 0) {
      setCurrentImageIndex(0);
    }
  }, [mediaItems, currentImageIndex, onMediaItemsChange]);

  const canAddMore = mediaItems.length < MAX_PHOTOS;
  const totalSlides = hasImages ? (canAddMore ? mediaItems.length + 1 : mediaItems.length) : 1;
  const isOnUploadSlide = hasImages && canAddMore && currentImageIndex === mediaItems.length;

  const canGoPrev = currentImageIndex > 0;
  const canGoNext = currentImageIndex < totalSlides - 1;

  const goToPrev = useCallback(() => {
    if (canGoPrev) setCurrentImageIndex(prev => prev - 1);
  }, [canGoPrev]);

  const goToNext = useCallback(() => {
    if (canGoNext) setCurrentImageIndex(prev => prev + 1);
  }, [canGoNext]);

  return (
    <View style={postItemStyles.container}>
      <StepHeader title={title} />
      
      <View style={postItemStyles.content}>
        <View style={styles.uploadAreaContainer}>
          <View style={styles.uploadArea}>
            {!hasImages || isOnUploadSlide ? (
              <TouchableOpacity
                style={styles.uploadPrompt}
                onPress={handleImagePick}
                activeOpacity={0.7}
              >
                <View style={styles.cameraIconContainer}>
                  <View style={styles.cameraIcon}>
                    <View style={styles.cameraBody} />
                    <View style={styles.cameraLens} />
                  </View>
                  <View style={styles.plusIcon}>
                    <View style={styles.plusVertical} />
                    <View style={styles.plusHorizontal} />
                  </View>
                </View>
                
                <View style={styles.uploadTextContainer}>
                  <Text style={styles.uploadText}>Add photo or video</Text>
                  <TouchableOpacity onPress={handleImagePick}>
                    <Text style={styles.uploadLink}>Click to upload</Text>
                  </TouchableOpacity>
                </View>
                
                <Text style={styles.uploadHint}>
                  Max file size {MAX_FILE_SIZE_MB}mb • Max {MAX_PHOTOS} files
                </Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.imagePreviewContainer}>
                <Image
                  source={{ uri: mediaItems[currentImageIndex]?.uri }}
                  style={styles.previewImage}
                  resizeMode="cover"
                />
                <TouchableOpacity
                  style={styles.deleteButton}
                  onPress={handleDeleteImage}
                  activeOpacity={0.7}
                >
                  <TrashIcon width={20} height={20} color={MARKETPLACE_COLORS.textPrimary} />
                </TouchableOpacity>
              </View>
            )}
          </View>
          
          <View style={styles.dotsBar}>
            {totalSlides > 1 ? (
              <View style={styles.navRow}>
                <TouchableOpacity
                  onPress={goToPrev}
                  disabled={!canGoPrev}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  style={!canGoPrev && styles.arrowDisabled}
                >
                  <ChevronLeftIcon
                    width={20}
                    height={20}
                    color={canGoPrev ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted}
                  />
                </TouchableOpacity>

                <CarouselDots
                  count={totalSlides}
                  activeIndex={currentImageIndex}
                  onDotPress={setCurrentImageIndex}
                  showBackground={false}
                  containerStyle={styles.dotsContainerOverride}
                />

                <TouchableOpacity
                  onPress={goToNext}
                  disabled={!canGoNext}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  style={!canGoNext && styles.arrowDisabled}
                >
                  <ChevronRightIcon
                    width={20}
                    height={20}
                    color={canGoNext ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted}
                  />
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  uploadAreaContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  uploadArea: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    width: '100%',
    height: UPLOAD_AREA_HEIGHT,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadPrompt: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    gap: 20,
  },
  cameraIconContainer: {
    width: 48,
    height: 48,
    position: 'relative',
  },
  cameraIcon: {
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraBody: {
    width: 36,
    height: 28,
    borderWidth: 2,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 4,
    position: 'relative',
  },
  cameraLens: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderWidth: 2,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 7,
  },
  plusIcon: {
    position: 'absolute',
    right: 0,
    top: 7,
    width: 13,
    height: 13,
  },
  plusVertical: {
    position: 'absolute',
    left: 5,
    top: 0,
    width: 2.5,
    height: 12.5,
    backgroundColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 3,
  },
  plusHorizontal: {
    position: 'absolute',
    left: 0,
    top: 5,
    width: 12.5,
    height: 2.5,
    backgroundColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 3,
  },
  uploadTextContainer: {
    alignItems: 'center',
    gap: 6,
  },
  uploadText: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  uploadLink: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentBlue,
    lineHeight: 18,
  },
  uploadHint: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
    textAlign: 'center',
  },
  imagePreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsBar: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.uploadBorderWhite,
    height: 48,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  arrowDisabled: {
    opacity: 0.4,
  },
  dotsContainerOverride: {
    position: 'relative',
    bottom: 0,
  },
});
